/**
 * Admin service for managing users and system statistics
 * Handles all admin-level operations including user management and analytics
 *
 * @module adminService
 */

import { logger } from '@/utils/logger';
import { API_BASE_URL, API_ENDPOINTS } from '@/utils/constants';
import { getSession } from './authService';
import type { User, AdminStats, InviteUserData, UpdateUserData, AuditLogEntry, PaginatedResponse } from '@/types';

// ============================================================================
// AUTH HELPER
// ============================================================================

/**
 * Get authorization headers for API requests
 * @returns Headers object with Authorization bearer token
 * @throws Error if no session available
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getSession();

  if (!session?.idToken) {
    logger.error(`[WALRUS] adminService: no auth session available`);
    throw new Error('Authentication required');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.idToken}`,
  };
}

/**
 * Fetch all users from the system
 * Requires admin authentication
 *
 * @returns Promise resolving to array of users
 * @throws Error if fetch fails
 */
export async function getUsers(): Promise<User[]> {
  logger.debug('[WALRUS] adminService.getUsers: fetching all users');

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ADMIN_USERS_LIST}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.getUsers: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to fetch users: ${response.statusText}`);
    }

    const data = await response.json();
    logger.debug('[WALRUS] adminService.getUsers: successfully fetched users', { count: data.data?.length || 0 });

    return data.data || [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.getUsers: error fetching users', { error: errorMessage });
    throw error;
  }
}

/**
 * Invite a new user to the system
 * Creates a new user account with temporary password
 *
 * @param data Invite user data (email, displayName, role)
 * @returns Promise resolving to created user
 * @throws Error if invitation fails
 */
export async function inviteUser(data: InviteUserData): Promise<User> {
  logger.debug('[WALRUS] adminService.inviteUser: inviting user', { email: data.email });

  try {
    const headers = await getAuthHeaders();

    // POST /admin/users - matches Lambda endpoint for inviting new users
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ADMIN_USERS_LIST}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.inviteUser: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to invite user: ${response.statusText}`);
    }

    const result = await response.json();
    logger.info('[WALRUS] adminService.inviteUser: user successfully invited', { email: data.email });

    return result.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.inviteUser: error inviting user', { email: data.email, error: errorMessage });
    throw error;
  }
}

/**
 * Update user information
 * Admins can update display name, role, and enable/disable status
 *
 * @param userId User ID to update
 * @param updates Update data (displayName, role, isEnabled)
 * @returns Promise resolving to updated user
 * @throws Error if update fails
 */
export async function updateUser(userId: string, updates: UpdateUserData): Promise<User> {
  logger.debug('[WALRUS] adminService.updateUser: updating user', { userId, updates });

  try {
    const headers = await getAuthHeaders();

    // PUT /admin/users/{id} - matches Lambda endpoint
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.updateUser: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to update user: ${response.statusText}`);
    }

    const result = await response.json();
    logger.info('[WALRUS] adminService.updateUser: user successfully updated', { userId });

    return result.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.updateUser: error updating user', { userId, error: errorMessage });
    throw error;
  }
}

/**
 * Delete a user from the system
 * Permanently removes user account
 *
 * @param userId User ID to delete
 * @returns Promise resolving to success
 * @throws Error if deletion fails
 */
export async function deleteUser(userId: string): Promise<void> {
  logger.debug('[WALRUS] adminService.deleteUser: deleting user', { userId });

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.deleteUser: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to delete user: ${response.statusText}`);
    }

    logger.info('[WALRUS] adminService.deleteUser: user successfully deleted', { userId });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.deleteUser: error deleting user', { userId, error: errorMessage });
    throw error;
  }
}

/**
 * Enable a user account
 * Reactivates a disabled user account using Cognito AdminEnableUser
 *
 * @param userId User ID to enable
 * @returns Promise resolving to success message
 * @throws Error if enable fails
 */
export async function enableUser(userId: string): Promise<void> {
  logger.debug('[WALRUS] adminService.enableUser: enabling user', { userId });

  try {
    const headers = await getAuthHeaders();

    // POST /admin/users/{id}/enable - matches Lambda endpoint
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/enable`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.enableUser: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to enable user: ${response.statusText}`);
    }

    logger.info('[WALRUS] adminService.enableUser: user enabled successfully', { userId });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.enableUser: error enabling user', { userId, error: errorMessage });
    throw error;
  }
}

/**
 * Disable a user account
 * Deactivates a user account using Cognito AdminDisableUser
 *
 * @param userId User ID to disable
 * @returns Promise resolving to success message
 * @throws Error if disable fails
 */
export async function disableUser(userId: string): Promise<void> {
  logger.debug('[WALRUS] adminService.disableUser: disabling user', { userId });

  try {
    const headers = await getAuthHeaders();

    // POST /admin/users/{id}/disable - matches Lambda endpoint
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/disable`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.disableUser: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to disable user: ${response.statusText}`);
    }

    logger.info('[WALRUS] adminService.disableUser: user disabled successfully', { userId });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.disableUser: error disabling user', { userId, error: errorMessage });
    throw error;
  }
}

/**
 * Get admin dashboard statistics
 * Returns system-wide metrics and usage data
 *
 * @returns Promise resolving to admin stats
 * @throws Error if fetch fails
 */
export async function getStats(): Promise<AdminStats> {
  logger.debug('[WALRUS] adminService.getStats: fetching admin statistics');

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}/admin/stats`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.getStats: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to fetch stats: ${response.statusText}`);
    }

    const data = await response.json();
    logger.debug('[WALRUS] adminService.getStats: successfully fetched stats', { stats: data.data });

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.getStats: error fetching stats', { error: errorMessage });
    throw error;
  }
}

/**
 * Get activity log with pagination
 * Returns recent admin and system actions
 *
 * @param page Page number (0-indexed, default: 0)
 * @param limit Number of items per page (default: 20)
 * @returns Promise resolving to paginated activity log
 * @throws Error if fetch fails
 */
export async function getActivityLog(page: number = 0, limit: number = 20): Promise<PaginatedResponse<AuditLogEntry>> {
  logger.debug('[WALRUS] adminService.getActivityLog: fetching activity log', { page, limit });

  try {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const response = await fetch(`${API_BASE_URL}/admin/activity?${queryParams}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      logger.warn('[WALRUS] adminService.getActivityLog: API returned non-OK status', { status: response.status });
      throw new Error(`Failed to fetch activity log: ${response.statusText}`);
    }

    const data = await response.json();
    logger.debug('[WALRUS] adminService.getActivityLog: successfully fetched activity log', {
      count: data.data?.items?.length || 0,
      page,
    });

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('[WALRUS] adminService.getActivityLog: error fetching activity log', { error: errorMessage });
    throw error;
  }
}
